<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:file-up',
	name: '$t:blocks.input_textarea_file.name',
	description: '$t:blocks.input_textarea_file.description',
	category: 'input',
	hidden: true,
	options: [
		{ name: 'modelValue', label: '$t:blocks.common.props.value', model: true },
		{ name: 'placeholder', label: '$t:blocks.common.props.placeholder' },
		{ name: 'autoresize', label: '$t:blocks.common.props.autoresize' },
		{ name: 'variant', label: '$t:blocks.common.props.variant', type: 'enum', values: BLOCK_INPUT_VARIANT_OPTIONS },
		{ name: 'size', label: '$t:blocks.common.props.size', type: 'enum', values: BLOCK_CONTROL_SIZE_OPTIONS },
		{ name: 'leadingIcon', label: '$t:blocks.common.props.leading_icon', block: BuiltinBlockIds.InputIcon, nullable: true },
		{ name: 'trailingIcon', label: '$t:blocks.common.props.trailing_icon', block: BuiltinBlockIds.InputIcon, nullable: true },
		{ name: 'loading', label: '$t:blocks.common.props.loading' },
		{ name: 'disabled', label: '$t:blocks.common.props.disabled' },
		{ name: 'readonly', label: '$t:blocks.common.props.readonly' },
		{ name: 'disabledNotice', label: '$t:blocks.common.props.disabled_notice' },
		{ name: 'acceptExtensions', label: '$t:blocks.input_textarea_file.props.accept_extensions' },
	],
}
</script>
<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { TextareaProps } from '#layers/ui/public';
import { createReusableTemplate } from '@vueuse/core';
import { useForwardProps } from 'reka-ui';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { decodeBase64, encodeBase64 as encodeBase64Value, isNullishOrEmpty } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface Props extends InputBlockProps, Omit<TextareaProps, 'readonly' | 'color'> {
	autoresize?: boolean;
	acceptExtensions?: string[];
	encodeBase64?: boolean;
}

const {
	autoresize = true,
	encodeBase64 = false,
	acceptExtensions,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	...forwardedProps
} = defineProps<Props>();

;

const model = defineModel<string>();
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model });
const selectedFile = ref<File | null>(null);
// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- decodes the initial model once; later edits flow through handlers
const textValue = ref<string>(encodeBase64 ? decodeBase64(modelValue.value ?? '') : modelValue.value ?? '');

const { t } = useI18n();
const forwarded = useForwardProps(() => ({ ...forwardedProps, autoresize, encodeBase64, disabledNotice: disabledNoticeProp }));
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const acceptString = computed(() => {
	if (isNullishOrEmpty(acceptExtensions)) return;
	return acceptExtensions.map((extension) => `.${extension}`).join(', ');
});

function removeFile() {
	selectedFile.value = null;
	modelValue.value = '';
}

async function handleFileChange() {
	// File has been deleted (deselected)
	if (!selectedFile.value) {
		modelValue.value = '';
		return;
	}

	if (encodeBase64) {
		const arrayBuffer = await selectedFile.value.arrayBuffer();
		const base64 = encodeBase64Value(String.fromCodePoint(...new Uint8Array(arrayBuffer)));
		modelValue.value = base64;
	}
	else {
		const text = await selectedFile.value.text();
		modelValue.value = text;
	}

	// Reset the raw model value (to avoid text being shown when the model value changed to a file)
	textValue.value = '';
}

function handleTextChange() {
	modelValue.value = encodeBase64 ? encodeBase64Value(textValue.value) : textValue.value;
	selectedFile.value = null;
}

const items = computed(() => [
	{ label: t('common.paste'), icon: 'lucide:clipboard', slot: 'paste', disabled: disabled.value },
	{ label: t('common.file'), icon: 'lucide:file-up', slot: 'file', disabled: disabled.value },
]);

const [DefineContentTextarea, ReuseContentTextarea] = createReusableTemplate<{
	rootClass: string;
	readonly: boolean;
}>();
</script>

<template>
	<DTooltip :disabled="!showDisabledNotice" :text="disabledNotice">
		<DefineContentTextarea v-slot="{ rootClass, readonly: textareaReadonly }">
			<DTextarea
				v-bind="forwarded"
				v-model="textValue"
				:class="rootClass"
				:disabled
				:readonly="textareaReadonly"
				:aria-readonly="textareaReadonly"
				:ui="{
					root: 'flex flex-col',
					base: 'flex-1',
				}"
				:placeholder="$t('blocks.input_textarea_file.paste_placeholder')"
				v-on="textareaReadonly ? {} : { keyup: handleTextChange }"
			/>
		</DefineContentTextarea>

		<div v-if="readonly" class="flex w-full flex-col rounded-md border p-2">
			<ReuseContentTextarea root-class="min-h-48 w-full" readonly />
		</div>
		<div v-else class="flex w-full flex-col rounded-md border p-2">
			<DTabs
				:items
				class="h-full"
				:ui="{ trigger: 'grow' }"
				:disabled
				variant="link"
			>
				<template #file>
					<div class="flex h-48">
						<DFileUpload
							v-model="selectedFile"
							:accept="acceptString"
							:multiple="false"
							:label="$t('blocks.input_textarea_file.dropzone_title')"
							:description="$t('blocks.input_textarea_file.dropzone_description', { extensions: acceptString })"
							:disabled
							layout="grid"
							variant="area"
							position="inside"
							class="flex-1"
							:ui="{
								file: 'flex flex-col items-center justify-center gap-2',
							}"
							@change="handleFileChange"
						>
							<template #file>
								<DIcon name="lucide:file" size="40" />
								<span class="text-sm font-medium">
									{{ selectedFile?.name }}
								</span>
								<DButton
									variant="outline"
									color="neutral"
									:label="$t('blocks.input_textarea_file.clear')"
									@click.stop="removeFile"
								/>
							</template>
						</DFileUpload>
					</div>
				</template>
				<template #paste>
					<div class="flex h-48">
						<ReuseContentTextarea root-class="flex flex-1" :readonly="false" />
					</div>
				</template>
			</DTabs>
		</div>
	</DTooltip>
</template>
